/**
 * "Who do I ask?": the routing table. Each topic names its first contacts and
 * its backup; any contact the chart does not itself prove is marked
 * "To be confirmed" in words. "Show on chart" opens the tree to that person.
 */
import React from 'react';
import { OrgChart, OrgContact } from '../../services/orgChartApi';
import { ConfirmMarker } from './OrgTree';
import { byId, displayName, matchesRoute, routeHasInferred } from './orgModel';

type Props = { chart: OrgChart; query: string; onShow: (id: string) => void };

function Contacts({ label, contacts, chart, onShow }: { label: string; contacts: OrgContact[]; chart: OrgChart; onShow: (id: string) => void }) {
  const index = byId(chart);
  if (!contacts.length) return null;
  return (
    <div className="oc-ask__group">
      <h4 className="oc-ask__label">{label}</h4>
      <ul className="oc-ask__people">
        {contacts.map((c) => {
          const p = index.get(c.position);
          if (!p) return null;
          return (
            <li key={c.position}>
              <span className="oc-ask__who">{displayName(p)}</span>
              <span className="oc-ask__as">{c.as}</span>
              {c.inferred && <ConfirmMarker />}
              <button type="button" className="oc-link" onClick={() => onShow(p.id)}>
                Show on chart<span className="oc-sr">: {displayName(p)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function WhoToAsk({ chart, query, onShow }: Props) {
  const topics = chart.routing.filter((r) => matchesRoute(r, query, chart));
  return (
    <section className="oc-ask" aria-labelledby="oc-ask-title">
      <h2 id="oc-ask-title" className="oc-h2">Who do I ask?</h2>
      <p className="oc-ask__intro">
        Ask the first contact. If they do not answer, ask the backup, then the person they report to on the chart.
      </p>
      {topics.length === 0 && <p className="oc-empty">No topic matches “{query}”.</p>}
      <ul className="oc-ask__topics">
        {topics.map((r) => (
          <li key={r.id} className="oc-ask__topic">
            <h3 className="oc-ask__name">
              {r.topic}
              {routeHasInferred(r) && <> <ConfirmMarker text="Partly to be confirmed" /></>}
            </h3>
            {r.examples && r.examples.length > 0 && (
              <p className="oc-ask__eg">For example: {r.examples.join('; ')}.</p>
            )}
            <Contacts label="Ask first" contacts={r.primary} chart={chart} onShow={onShow} />
            <Contacts label="Backup" contacts={r.backup} chart={chart} onShow={onShow} />
            {r.backup.length === 0 && <p className="oc-ask__eg">No backup named: ask the person they report to on the chart.</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}
